import { ReactNode } from 'react'

import isNil from 'lodash/isNil'

import {
    RemoveSovKeywordFromLabelField,
    SovStateField,
} from 'components/EditableFields/SovFields'
import { SovBadgesJsonCell } from 'components/FieldRenderers/SovBadgesJsonCell/SovBadgesJsonCell'
import { ResearchKeywordLink } from 'components/Links/ResearchKeywordLink/ResearchKeywordLink'
import { ResourceLink } from 'components/Links/ResourceLink'
import { UNDEFINED_VALUE } from 'const/formatting'
import { isUnset } from 'helpers/utilities'
import { labelUrl, sovKeywordUrl } from 'routing/urls'
import {
    CellRenderProps,
    Label,
    SovBadge,
    SovFact,
    SovKeywordGroupBy,
} from 'types'

import * as styles from './styles.scss'

type RenderProps<RecordType> = {
    cellRenderProps: CellRenderProps<RecordType>
    dataIndex: string[]
}

export function sovKeywordIdRenderer<RecordType extends SovKeywordGroupBy>({
    value,
    record,
    undefinedValue = UNDEFINED_VALUE,
}: CellRenderProps<RecordType>): ReactNode {
    if (isUnset(value)) {
        return undefinedValue
    }

    return (
        <div className={styles['field-with-actions']}>
            <ResourceLink className={styles.text} to={sovKeywordUrl(value)}>
                {record.keyword.text}
            </ResourceLink>
            <ResearchKeywordLink
                countryCode={record.keyword.country_code}
                keyword={record.keyword.text}
            />
        </div>
    )
}

export function sovKeywordStateRenderer<RecordType extends SovKeywordGroupBy>({
    cellRenderProps: { record, readonly },
    dataIndex,
}: RenderProps<RecordType>): ReactNode {
    return (
        <SovStateField
            record={record}
            readOnly={readonly}
            dataIndex={dataIndex}
        />
    )
}

export function sovKeywordLabelIdRenderer<
    RecordType extends SovKeywordGroupBy & {
        keyword__label: Label
    },
>({
    value,
    record,
    undefinedValue = UNDEFINED_VALUE,
}: CellRenderProps<RecordType>): ReactNode {
    if (isUnset(value)) {
        return undefinedValue
    }

    return (
        <ResourceLink
            to={{
                pathname: labelUrl(value),
                search: '?tab=sovKeywords',
            }}
        >
            {record.keyword__label?.name}
        </ResourceLink>
    )
}

export function removeSovKeywordFromLabelRenderer<RecordType extends SovFact>({
    cellRenderProps: { reloadData, readonly, record },
    dataIndex,
}: RenderProps<RecordType>): ReactNode {
    return (
        <RemoveSovKeywordFromLabelField
            dataIndex={dataIndex}
            record={record}
            readOnly={readonly}
            reloadData={reloadData}
        />
    )
}

export const badgesJsonRenderer = <RecordType,>({
    value,
}: CellRenderProps<RecordType>): ReactNode => {
    if (isNil(value)) {
        return UNDEFINED_VALUE
    }

    let parsedValue: Record<SovBadge, boolean>
    try {
        parsedValue = JSON.parse(value)
    } catch {
        return UNDEFINED_VALUE
    }

    const activeBadges = Object.entries(parsedValue)
        .filter(([_badge, isActive]) => isActive)
        .map(([badge, _isActive]) => badge as SovBadge)

    if (activeBadges.length === 0) {
        return UNDEFINED_VALUE
    }

    return <SovBadgesJsonCell badges={activeBadges} />
}
